import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardFooter } from "../ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Calendar, MapPin, MessageSquare, MinusCircle, Pencil, UserPlus, Search, Footprints, Target, Save, X, Trash2, ChevronDown, ArrowUpDown, Filter, XCircle } from "lucide-react";
import { Session, User, Dog } from "../../types";
import { useDroppable } from '@dnd-kit/core';
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Badge } from "../ui/badge";
import { updateSession, deleteSession } from "../../utils/api";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { Command, CommandGroup, CommandItem } from "../ui/command";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import CanicrossIcon from "../icon/CanicrossIcon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import DogHomePageIcon from "../icon/DogHomePageIcon";

interface SessionsListProps {
  sessions: Session[];
  onDogClick?: (dogId: string) => void; // Make optional if not always needed
  onSessionClick?: (sessionId: string) => void;
  selectedSessionId?: string;
  onSessionUpdate?: (session: Session) => void;
  onRemoveDog?: (sessionId: string, dogId: string) => void; // This was missing
  dashboardTrainers?: User[];
  canEdit?: boolean;
}

const activityDisplayConfig: { [key: string]: { label: string; icon: React.ElementType; color: string; bgColor: string; borderColor: string; } } = {
  mantrailing: { label: "Mantrailing", icon: Search, color: "blue", bgColor: "bg-blue-500/8", borderColor: "border-blue-500" },
  hiking: { label: "Randonnée", icon: Footprints, color: "green", bgColor: "bg-green-500/8", borderColor: "border-green-500" },
  canicross: { label: "Canicross", icon: CanicrossIcon, color: "orange", bgColor: "bg-orange-500/8", borderColor: "border-orange-500" },
};

const SessionCard = ({ session, isOver, selectedSessionId, onSessionClick, onSessionUpdate, onRemoveDog, dashboardTrainers, canEdit }: Omit<SessionsListProps,'sessions'> & { session: Session, isOver: boolean }) => {
  const activityConfig = activityDisplayConfig[session.activityType] || activityDisplayConfig.mantrailing;

  // States for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editedLocation, setEditedLocation] = useState(session.location || "");
  const [editedComment, setEditedComment] = useState(session.comment || "");
  const [editedStartTime, setEditedStartTime] = useState(new Date(session.startTime).toISOString().slice(0, 16));
  const [editedEndTime, setEditedEndTime] = useState(new Date(session.endTime).toISOString().slice(0, 16));
  const [editingObjectiveForDog, setEditingObjectiveForDog] = useState<string | null>(null);
  const [objectiveText, setObjectiveText] = useState("");

  const cardClasses = `group relative cursor-pointer border-2 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 ${activityConfig.bgColor} ${activityConfig.borderColor} ${selectedSessionId === session._id ? 'ring-2 ring-primary' : ''}`;

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onSessionUpdate || !canEdit) return;

    if (new Date(editedStartTime).getTime() >= new Date(editedEndTime).getTime()) {
      toast.error("L'heure de début doit être antérieure à l'heure de fin.");
      return;
    }

    try {
      const updatedSession = await updateSession(session._id, {
        location: editedLocation,
        comment: editedComment,
        startTime: editedStartTime,
        endTime: editedEndTime,
      });
      onSessionUpdate?.(updatedSession);
      setIsEditing(false);
      toast.success("Session mise à jour.");
    } catch {
      toast.error("Erreur lors de la mise à jour de la session.");
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditedLocation(session.location || "");
    setEditedComment(session.comment || "");
    setEditedStartTime(new Date(session.startTime).toISOString().slice(0, 16));
    setEditedEndTime(new Date(session.endTime).toISOString().slice(0, 16));
  };

  const handleObjectiveClick = (e: React.MouseEvent, dogId: string, currentObjective: string) => {
    e.stopPropagation();
    setEditingObjectiveForDog(dogId);
    setObjectiveText(currentObjective);
  };

  const handleSaveObjective = async (e: React.MouseEvent, dogIdToUpdate: string) => {
    e.stopPropagation();
    if (!onSessionUpdate) return;

    const newPlannedDogs = session.plannedDogs.map(pd =>
      pd.dog._id === dogIdToUpdate ? { ...pd, objective: objectiveText } : pd
    ).map(pd => ({ dog: pd.dog._id, objective: pd.objective }));

    try {
      const updatedSession = await updateSession(session._id, { plannedDogs: newPlannedDogs });
      onSessionUpdate(updatedSession);
      setEditingObjectiveForDog(null);
      toast.success("Objectif mis à jour.");
    } catch {
      toast.error("Erreur lors de la mise à jour de l'objectif.");
    }
  };

  const handleCancelObjectiveEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingObjectiveForDog(null);
  };

  const handleAssignTrainer = async (trainerId: string) => {
    if (!onSessionUpdate) return;
    const isAssigned = session.assignedTrainers.some(t => t._id === trainerId);
    const newAssignedTrainers = isAssigned
      ? session.assignedTrainers.filter(t => t._id !== trainerId).map(t => t._id)
      : [...session.assignedTrainers.map(t => t._id), trainerId];

    try {
      const updatedSession = await updateSession(session._id, { assignedTrainers: newAssignedTrainers });
      onSessionUpdate(updatedSession);
      toast.success(`Moniteurs mis à jour.`);
    } catch {
      toast.error("Erreur lors de la mise à jour des moniteurs.");
    }
  };

  const handleDeleteSession = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit) return;

    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette session ?")) {
      try {
        await deleteSession(session._id);
        toast.success("Session supprimée.");
        // The parent component will receive the websocket event and remove the session from the list.
      } catch {
        toast.error("Erreur lors de la suppression de la session.");
      }
    }
  };
  return (
    <Card className={cardClasses} style={{ borderLeftWidth: '8px' }} onClick={() => onSessionClick?.(session._id)}>
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {session.assignedTrainers.slice(0, 3).map(trainer => (
                  <Avatar key={trainer._id} className="h-10 w-10 border-2 border-white">
                    <AvatarImage src={trainer.profilePicture} />
                    <AvatarFallback>{trainer.username.substring(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                ))}
                {session.assignedTrainers.length > 3 && (
                  <Avatar className="h-10 w-10 border-2 border-white">
                    <AvatarFallback>+{session.assignedTrainers.length - 3}</AvatarFallback>
                  </Avatar>
                )}
              </div>
              {canEdit && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="icon" className="h-8 w-8 rounded-full" onClick={e => e.stopPropagation()}><UserPlus className="h-4 w-4" /></Button>
                  </PopoverTrigger>
                  <PopoverContent onClick={e => e.stopPropagation()}>
                    <Command>
                      <CommandGroup>
                        {dashboardTrainers?.map(trainer => (
                          <CommandItem key={trainer._id} onSelect={() => handleAssignTrainer(trainer._id)} className="flex items-center justify-between">
                            <span>{trainer.username}</span>
                            <Checkbox checked={session.assignedTrainers.some(t => t._id === trainer._id)} />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            {canEdit && (
              <div className="flex flex-col gap-1">
                {isEditing ? (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 bg-green-100 hover:bg-green-200" onClick={handleSave}><Save className="h-4 w-4 text-green-600" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 bg-red-100 hover:bg-red-200" onClick={handleCancelEdit}><X className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ) : (
                  <>
                    <Button variant="secondary" size="icon" className="h-8 w-8 shadow-md" onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="destructive" size="icon" className="h-8 w-8 shadow-md" onClick={handleDeleteSession}><Trash2 className="h-4 w-4" /></Button>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`bg-${activityConfig.color}-500 text-white text-xs px-2 py-0.5`}>{activityConfig.label}</Badge>
            {isEditing ? (
              <Input value={editedLocation} onChange={(e) => setEditedLocation(e.target.value)} onClick={e => e.stopPropagation()} className="text-sm mt-1" placeholder="Lieu..." />
            ) : (
              <p className="text-sm text-muted-foreground flex items-center justify-end gap-1 mt-1 truncate">
                <MapPin className="h-3.5 w-3.5" />
                {session.location || "Lieu non défini"}
              </p>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isEditing ? (
          <div className="border-t pt-3 space-y-2">
            <Input type="datetime-local" value={editedStartTime} onChange={(e) => setEditedStartTime(e.target.value)} onClick={e => e.stopPropagation()} className="w-full text-sm" />
            <Input type="datetime-local" value={editedEndTime} onChange={(e) => setEditedEndTime(e.target.value)} onClick={e => e.stopPropagation()} className="w-full text-sm" />
          </div>
        ) : (
          <div className="border-t pt-3 space-y-1">
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Début: {new Date(session.startTime).toLocaleString("fr-FR", { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Fin: {new Date(session.endTime).toLocaleString("fr-FR", { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          </div>
        )}

        <div className="border-t pt-3 space-y-2">
          <div className="flex justify-between items-center">
            <p className="text-sm font-medium flex items-center gap-2">
              <DogHomePageIcon className="h-4 w-4" />
              {session.plannedDogs.length} chien{session.plannedDogs.length !== 1 ? 's' : ''}
            </p>
            <div className="flex -space-x-2">
              {session.plannedDogs.filter(pd => pd.dog).map(({ dog }) => (
                <Avatar key={dog._id} className="h-7 w-7 border-2 border-white">
                  <AvatarImage src={dog.profilePhoto} />
                  <AvatarFallback>{dog.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
              ))}
            </div>
          </div>
          {session.plannedDogs.length > 0 ? (
            <div className="space-y-2">
              {session.plannedDogs.filter(pd => pd.dog).slice(0, 2).map(({ dog, objective }) => (
                <div key={dog._id} className="bg-white/50 rounded-lg p-2 group/dog">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-bold">{dog.name}</p>
                    {canEdit && (
                      <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full opacity-0 group-hover/dog:opacity-100" onClick={(e) => { e.stopPropagation(); onRemoveDog?.(session._id, dog._id); }}>
                        <MinusCircle className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                  {editingObjectiveForDog === dog._id ? (
                    <div className="mt-2 space-y-1">
                      <Textarea value={objectiveText} onChange={e => setObjectiveText(e.target.value)} onClick={e => e.stopPropagation()} placeholder="Objectif..." className="text-xs" />
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={e => handleSaveObjective(e, dog._id)}><Save className="h-3 w-3 text-green-600" /></Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCancelObjectiveEdit}><X className="h-3 w-3 text-destructive" /></Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground italic flex items-center gap-1 truncate cursor-pointer" onClick={(e) => handleObjectiveClick(e, dog._id, objective)}>
                      {objective ? (
                        <>
                          <Target className="h-3 w-3 flex-shrink-0" />
                          {objective}
                        </>
                      ) : (
                        <span className="flex items-center gap-1 not-italic text-slate-400"><Pencil className="h-3 w-3" />Ajouter un objectif...</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {session.plannedDogs.length > 2 && (
                <p className="text-xs text-muted-foreground pl-2">+ {session.plannedDogs.length - 2} autre(s) chien(s)</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic border-t pt-3 flex items-center gap-2">
              <DogHomePageIcon className="h-4 w-4" />
              Aucun chien assigné
            </p>
          )}
        </div>
      </CardContent>
      <CardFooter className="border-t pt-3">
        {isEditing ? (
          <div className="w-full space-y-1">
            <Label htmlFor={`comment-${session._id}`} className="text-xs text-muted-foreground">Commentaire</Label>
            <Textarea id={`comment-${session._id}`} value={editedComment} onChange={(e) => setEditedComment(e.target.value)} onClick={e => e.stopPropagation()} placeholder="Ajouter un commentaire..." />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic flex items-start gap-2 w-full">
            <MessageSquare className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-2">
              {session.comment || <span className="not-italic text-slate-400">Aucun commentaire</span>}
            </span>
          </p>
        )}
      </CardFooter>
    </Card>
  );
};

const DroppableSessionCard = (props: Omit<React.ComponentProps<typeof SessionCard>, 'isOver'>) => {
  const { setNodeRef, isOver } = useDroppable({ id: props.session._id });
  return <div ref={setNodeRef}><SessionCard {...props} isOver={isOver} /></div>;
};

const Filters = ({
  sessions,
  dashboardTrainers,
  onFilterChange,
  onSortChange,
  sortOrder,
}: {
  sessions: Session[];
  dashboardTrainers?: User[];
  onFilterChange: (filters: any) => void;
  onSortChange: (order: 'asc' | 'desc') => void;
  sortOrder: 'asc' | 'desc';
}) => {
  const [filters, setFilters] = useState({
    dog: 'all',
    trainer: 'all',
    activity: 'all',
    status: 'all',
  });

  const allDogs = useMemo(() => {
    const dogMap = new Map<string, Dog>();
    sessions.forEach(session => {
      session.plannedDogs.forEach(pd => {
        if (pd.dog) {
          dogMap.set(pd.dog._id, pd.dog);
        }
      });
    });
    return Array.from(dogMap.values());
  }, [sessions]);

  const handleFilter = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters = { dog: 'all', trainer: 'all', activity: 'all', status: 'all' };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== 'all');

  return (
    <div className="p-4 border rounded-lg bg-muted/50 mb-6 space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Filter className="h-5 w-5" />
          <span>Filtres & Tri</span>
        </div>
        {hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters}><XCircle className="mr-2 h-4 w-4" />Effacer</Button>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Sort Control */}
        <div className="space-y-1.5">
          <Label>Trier par date</Label>
          <Button variant="outline" className="w-full" onClick={() => onSortChange(sortOrder === 'asc' ? 'desc' : 'asc')}>
            <ArrowUpDown className="mr-2 h-4 w-4" />
            {sortOrder === 'asc' ? 'Croissant' : 'Décroissant'}
          </Button>
        </div>
        {/* Status Filter */}
        <div className="space-y-1.5">
         <Label>Passées/À venir</Label>
        <Select value={filters.status} onValueChange={(v) => handleFilter('status', v)}>
          <SelectTrigger><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="upcoming">À venir</SelectItem>
            <SelectItem value="past">Passées</SelectItem>
          </SelectContent>
        </Select>
        </div>
        {/* Activity Filter */}
        <div className="space-y-1.5">
          <Label>Activité</Label>
        <Select value={filters.activity} onValueChange={(v) => handleFilter('activity', v)}>
          <SelectTrigger><SelectValue placeholder="Activité" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les activités</SelectItem>
            {Object.entries(activityDisplayConfig).map(([key, { label }]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        </div>
        {/* Trainer Filter */}
                <div className="space-y-1.5">
          <Label>Éducateurs</Label>
        <Select value={filters.trainer} onValueChange={(v) => handleFilter('trainer', v)}>
          <SelectTrigger><SelectValue placeholder="Éducateur" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les éducateurs</SelectItem>
            {dashboardTrainers?.map(t => <SelectItem key={t._id} value={t._id}>{t.username}</SelectItem>)}
          </SelectContent>
        </Select>
        </div>
        {/* Dog Filter */}
                <div className="space-y-1.5">
          <Label>Chiens</Label>
        <Select value={filters.dog} onValueChange={(v) => handleFilter('dog', v)}>
          <SelectTrigger><SelectValue placeholder="Chien" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les chiens</SelectItem>
            {allDogs.map(d => <SelectItem key={d._id} value={d._id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        </div>
      </div>
    </div>
  );
};

export const SessionsList: React.FC<SessionsListProps> = ({ sessions, onDogClick, onSessionClick, selectedSessionId, onSessionUpdate, onRemoveDog, dashboardTrainers, canEdit }) => {
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [activeFilters, setActiveFilters] = useState({ dog: 'all', trainer: 'all', activity: 'all', status: 'all' });

  const filteredAndSortedSessions = useMemo(() => {
    let processedSessions = [...sessions];

    // Filtering
    if (activeFilters.status !== 'all') {
      const now = new Date();
      processedSessions = processedSessions.filter(s =>
        activeFilters.status === 'upcoming' ? new Date(s.startTime) >= now : new Date(s.startTime) < now
      );
    }
    if (activeFilters.activity !== 'all') {
      processedSessions = processedSessions.filter(s => s.activityType === activeFilters.activity);
    }
    if (activeFilters.trainer !== 'all') {
      processedSessions = processedSessions.filter(s => s.assignedTrainers.some(t => t._id === activeFilters.trainer));
    }
    if (activeFilters.dog !== 'all') {
      processedSessions = processedSessions.filter(s => s.plannedDogs.some(pd => pd.dog?._id === activeFilters.dog));
    }

    // Sorting
    processedSessions.sort((a, b) => {
      const dateA = new Date(a.startTime).getTime();
      const dateB = new Date(b.startTime).getTime();
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return processedSessions;
  }, [sessions, activeFilters, sortOrder]);

  if (sessions.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Calendar className="mx-auto h-12 w-12 mb-4" />
        <h3 className="text-lg font-semibold">Aucune session planifiée</h3>
        <p>Cliquez sur "Créer une session" pour commencer.</p>
      </div>
    );
  }

  // Group sessions by date
  const groupedSessions = filteredAndSortedSessions.reduce((acc, session) => {
    const dateKey = new Date(session.startTime).toLocaleDateString("fr-FR", {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(session);
    return acc;
  }, {} as Record<string, Session[]>);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div>
      <Filters
        sessions={sessions}
        dashboardTrainers={dashboardTrainers}
        onFilterChange={setActiveFilters}
        onSortChange={setSortOrder}
        sortOrder={sortOrder}
      />
      <div className="space-y-6">
      {Object.entries(groupedSessions).map(([date, dateSessions]) => (
        <Collapsible key={date} defaultOpen={new Date(dateSessions[0].startTime) >= today}>
          <CollapsibleTrigger className="w-full flex items-center justify-between mb-3 group">
            <h2 className="text-lg font-semibold text-muted-foreground capitalize group-hover:text-primary transition-colors">{date}</h2>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{dateSessions.length} session{dateSessions.length > 1 ? 's' : ''}</Badge>
              <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform duration-300 group-data-[state=open]:rotate-180" />
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dateSessions.map((session) => (
                <DroppableSessionCard
                  key={session._id}
                  session={session}
                  selectedSessionId={selectedSessionId}
                  onSessionClick={onSessionClick}
                  onDogClick={onDogClick}
                  onSessionUpdate={onSessionUpdate}
                  onRemoveDog={onRemoveDog}
                  dashboardTrainers={dashboardTrainers}
                  canEdit={canEdit}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      ))}
      </div>
    </div>
  );
};
